import { useEffect } from 'react';

/** Rows j / k move through: table rows, list rows and live-list items inside the page. */
export const ROW_SELECTOR = 'main :is(tr.clickable, .rows > li > .row-link, a.list-item, .list-item > a.row-link)';

const typing = (el: Element | null) =>
  !!el && (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement || (el as HTMLElement).isContentEditable);

/** The next row to focus for j (+1) / k (−1), given the rows on the page and the focused element. */
export function nextRow(rows: HTMLElement[], active: Element | null, step: 1 | -1): HTMLElement | null {
  if (!rows.length) return null;
  const i = rows.findIndex((r) => r === active || r.contains(active));
  if (i < 0) return step === 1 ? rows[0] : rows[rows.length - 1];
  return rows[Math.min(rows.length - 1, Math.max(0, i + step))];
}

/**
 * Desktop keys: "/" puts the cursor in the page's search box, j / k move through the list, Enter opens the row (rows
 * and links handle Enter themselves). Esc is handled by each dialog. Nothing fires while typing or with a modifier.
 */
export function useShortcuts() {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.defaultPrevented) return;
      if (typing(document.activeElement)) {
        if (e.key === 'Escape') (document.activeElement as HTMLElement).blur();
        return;
      }
      if (document.querySelector('[role="dialog"]')) return;
      if (e.key === '/') {
        const box = document.querySelector<HTMLInputElement>('main .search input, main input[type="search"]');
        if (box) {
          e.preventDefault();
          box.focus();
          box.select();
        }
        return;
      }
      if (e.key === 'j' || e.key === 'k') {
        const rows = Array.from(document.querySelectorAll<HTMLElement>(ROW_SELECTOR)).filter((r) => r.offsetParent !== null);
        const next = nextRow(rows, document.activeElement, e.key === 'j' ? 1 : -1);
        if (next) {
          e.preventDefault();
          if (!next.hasAttribute('tabindex') && !(next instanceof HTMLAnchorElement) && !(next instanceof HTMLButtonElement)) next.tabIndex = 0;
          next.focus({ preventScroll: true });
          next.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
}
